import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Player from '@vimeo/player';
import { useEffect, useRef, useState } from 'react';

let youtubeApiReady = false;
let youtubeApiLoadedPromise;
function loadYouTubeAPI() {
    if (youtubeApiReady) return youtubeApiLoadedPromise;
    youtubeApiLoadedPromise = new Promise((resolve) => {
        window.onYouTubeIframeAPIReady = () => resolve();
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
    });
    youtubeApiReady = true;
    return youtubeApiLoadedPromise;
}

export default function WidgetView({
    widget,
    t,
    color,
    textButtonColor = '#FFFFFF',
    onClick,
    readonly = false,
    scaleFactor = 1,
}) {
    const [embeddableVideoUrl, setEmbeddableVideoUrl] = useState(undefined);
    const stackRef = useRef(null);
    const [height, setHeight] = useState(null);
    const videoType = getVideoType(widget.payload?.link?.url);
    /* YouTube */
    const [youtubeVideoId, setYoutubeVideoId] = useState(undefined);
    const youTubePlayerRef = useRef(null);
    /* Vimeo */
    const iframeRef = useRef(null);

    const aspectRatio = 540 / 236;

    useEffect(() => {
        const updateHeight = () => {
            if (stackRef.current) {
                const width = stackRef.current.offsetWidth;
                const calculatedHeight = width / aspectRatio;
                setHeight(calculatedHeight);
            }
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const handleDownload = () => {
        if (onClick) onClick(widget);
        const d = document.createElement('a');
        d.href = widget.payload.document.url;
        d.target = '_blank';
        d.click();
    };

    const widgetLinkUrl = widget.payload.link?.url;
    const imgWidget = (
        <img
            style={{
                cursor: widgetLinkUrl ? 'pointer' : 'auto',
                borderRadius: '16px',
                width: '100%',
                transform: `scale(${scaleFactor})`,
                transformOrigin: 'center',
            }}
            src={widget.payload.image?.url}
            alt="img"
        />
    );

    function trackVideoPlay(yPosition, xPosition) {
        if (readonly) return;
        if (onClick) onClick(widget);
    }
    useEffect(() => {
        if (videoType == 'VIMEO') {
            if (!embeddableVideoUrl || !iframeRef.current) return;
            const player = new Player(iframeRef.current);
            const onPlay = () => {
                trackVideoPlay(widget.yPosition, widget.xPosition);
            };
            player.on('play', onPlay);

            return () => {
                player.off('play', onPlay);
                player.unload().catch(() => {});
            };
        }
    }, [embeddableVideoUrl, videoType]);

    useEffect(() => {
        if (widget?.payload?.type === 'video') {
            if (videoType == 'YOUTUBE') {
                const youtubeId = extractYoutubeId(widget.payload.link.url);
                setYoutubeVideoId(youtubeId);
                setEmbeddableVideoUrl(undefined);
            } else if (videoType == 'VIMEO') {
                const url = convertIfVimeoEmbeddableVideoUrl(widget.payload.link.url);
                setEmbeddableVideoUrl(url);
            } else {
                setEmbeddableVideoUrl(widget.payload.link.url);
            }
        }
    }, [widget]);

    useEffect(() => {
        if (youtubeVideoId && youTubePlayerRef.current === null) {
            let player;
            loadYouTubeAPI().then(() => {
                player = new YT.Player(`yt-player-${widget.yPosition + '-' + widget.xPosition}`, {
                    videoId: youtubeVideoId,
                    width: '100%',
                    height,
                    playerVars: {
                        rel: 0,
                        modestbranding: 1,
                    },
                    events: {
                        onStateChange: (event) => {
                            if (event.data === YT.PlayerState.PLAYING) {
                                trackVideoPlay(widget.yPosition, widget.xPosition);
                            }
                        },
                    },
                });
                youTubePlayerRef.current = player;
            });
            return () => {
                if (youTubePlayerRef.current?.destroy) {
                    youTubePlayerRef.current.destroy();
                    youTubePlayerRef.current = null;
                }
            };
        }
    }, [youtubeVideoId, height]);

    const hasButton = Boolean(widget.payload.link?.label) || Boolean(widget.payload.document?.url);

    return (
        <>
            <Stack
                ref={stackRef}
                alignItems={'center'}
                sx={{
                    background: '#FFFFFF',
                    width: '100%',
                    maxWidth: '540px',
                    height: height ? `${height}px` : 'auto',
                    boxShadow: 1,
                    borderRadius: '20px',
                    justifyContent: 'center',
                    marginBottom: `${scaleFactor * 20}px`,
                    cursor: !readonly && !hasButton && widget.isLead ? 'pointer' : 'auto',
                }}
                onClick={!readonly && !hasButton ? () => onClick(widget) : undefined}
            >
                {widget.payload.type === 'image' && imgWidget}
                {widget.payload.type === 'text' && (
                    <Stack
                        direction={'column'}
                        alignSelf={'start'}
                        textAlign={'start'}
                        alignContent={'start'}
                        spacing={1 * scaleFactor}
                        pt={1 * scaleFactor}
                        sx={{
                            maxHeight: height,
                            height: '100%',
                            width: '100%',
                            maxWidth: 540,
                            marginBottom: `${scaleFactor * (readonly ? 12 : 24)}px`,
                        }}
                        justifyContent={'space-between'}
                        textOverflow={'ellipsis'}
                    >
                        <span
                            style={{
                                marginLeft: 16 * scaleFactor,
                                marginRight: 16 * scaleFactor,
                                marginTop: readonly ? 12 * scaleFactor : 0,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontSize: `${scaleFactor * 18}px !important`,
                                    wordBreak: 'break-word',
                                    lineHeight: 1.2,
                                    marginBottom: 1 * scaleFactor,
                                }}
                            >
                                {widget.payload.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: `${scaleFactor * 14}px !important`,
                                    lineHeight: 1.5,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {widget.payload.body}
                            </Typography>
                        </span>
                        {!readonly && hasButton && (
                            <span>
                                <Button
                                    onClick={() => onClick(widget)}
                                    variant="contained"
                                    sx={{
                                        background: color,
                                        borderColor: color,
                                        color: textButtonColor,
                                        marginLeft: `${scaleFactor * 16}px`,
                                        marginRight: `${scaleFactor * 16}px`,
                                        border: `1px solid ${textButtonColor}`,
                                        '&:hover': {
                                            borderColor: color,
                                            background: color,
                                        },
                                        transform: `scale(${scaleFactor})`,
                                        transformOrigin: 'center',
                                    }}
                                >
                                    {widget.payload.link.label}
                                </Button>
                            </span>
                        )}
                    </Stack>
                )}
                {/* Youtube videos */}
                {widget?.payload?.type === 'video' && videoType == 'YOUTUBE' && (
                    <div
                        style={{
                            width: '100%',
                            height,
                            borderRadius: 16,
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            id={`yt-player-${widget.yPosition + '-' + widget.xPosition}`}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                )}
                {/* Media files */}
                {widget?.payload?.type === 'video' && videoType == 'MEDIA' && embeddableVideoUrl && (
                    <video
                        controls
                        onPlay={() => trackVideoPlay(widget.yPosition, widget.xPosition)}
                        style={{
                            width: '100%',
                            height: `${height}px`,
                            objectFit: 'contain',
                            backgroundColor: 'black',
                            borderRadius: '16px',
                            transform: `scale(${scaleFactor})`,
                            transformOrigin: 'center',
                        }}
                    >
                        <source src={embeddableVideoUrl} />
                        Your browser does not support the video tag.
                    </video>
                )}
                {/* Other videos */}
                {widget?.payload?.type === 'video' &&
                    (videoType == 'VIMEO' || videoType == 'OTHER') &&
                    embeddableVideoUrl && (
                        <iframe
                            width="100%"
                            ref={videoType == 'VIMEO' ? iframeRef : undefined}
                            id={videoType == 'VIMEO' ? `vimeo-player-${widget.yPosition}-${widget.xPosition}` : ''}
                            height={height}
                            src={embeddableVideoUrl}
                            title="Video"
                            style={{ border: 0, borderRadius: 16 }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    )}

                {widget.payload.type === 'file' && (
                    <Stack
                        direction={'column'}
                        textAlign={'start'}
                        alignSelf={'start'}
                        alignContent={'start'}
                        spacing={1 * scaleFactor}
                        pt={1 * scaleFactor}
                        justifyContent={'space-between'}
                        sx={{
                            maxHeight: height,
                            height: '100%',
                            width: '100%',
                            marginBottom: `${scaleFactor * (readonly ? 12 : 24)}px`,
                        }}
                    >
                        <span
                            style={{
                                marginLeft: 16 * scaleFactor,
                                marginRight: 16 * scaleFactor,
                                marginTop: readonly ? 12 * scaleFactor : 0,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontSize: `${scaleFactor * 18}px !important`,
                                    wordBreak: 'break-word',
                                    lineHeight: 1.2,
                                    marginBottom: 1 * scaleFactor,
                                }}
                            >
                                {widget.payload.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: `${scaleFactor * 14}px !important`,
                                    lineHeight: 1.5,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {widget.payload.body}
                            </Typography>
                        </span>
                        {!readonly && hasButton && (
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleDownload}
                                    sx={{
                                        background: color,
                                        marginLeft: `${scaleFactor * 16}px`,
                                        marginRight: `${scaleFactor * 16}px`,
                                        color: textButtonColor,
                                        border: `1px solid ${textButtonColor}`,
                                        borderColor: color,
                                        '&:hover': {
                                            borderColor: color,
                                            background: color,
                                        },
                                        transform: `scale(${scaleFactor})`,
                                        transformOrigin: 'center',
                                    }}
                                >
                                    {t('pages.widget.download')}
                                </Button>
                            </span>
                        )}
                    </Stack>
                )}
            </Stack>
        </>
    );
}

const convertIfVimeoEmbeddableVideoUrl = (url) => {
    const vimeoRegex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
    const match = url.match(vimeoRegex);
    if (match) {
        const videoId = match[1];
        const hash = match[2] ? `?h=${match[2]}` : '';
        return `https://player.vimeo.com/video/${videoId}${hash}`;
    }
    return url;
};

const getVideoType = (url) => {
    if (!url) return undefined;
    if (extractYoutubeId(url)) return 'YOUTUBE';
    if (extractVimeoId(url)) return 'VIMEO';
    if (isVideoFile(url)) return 'MEDIA';
    return 'OTHER';
};
const extractVimeoId = (url) => {
    const vimeoRegex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : undefined;
};

const extractYoutubeId = (url) => {
    const regExp = /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]{11}).*/;
    const match = url.match(regExp);
    return match ? match[1] : undefined;
};

function isVideoFile(url) {
    return /\.(mp4|webm|ogg)(\?.*)?$/.test(url);
}
